// Cliente da consulta pública do DJEN (CNJ). Confira os nomes dos campos no Swagger oficial:
// https://comunicaapi.pje.jus.br/swagger/index.html  (o leitor abaixo é defensivo de propósito)
import { ymd, br } from '../../lib/date';

const BASE = 'https://comunicaapi.pje.jus.br/api/v1/comunicacao';

export type Parte = { nome: string; polo: string };   // polo: 'A' (ativo), 'P' (passivo) ou outro texto
export type Pub = { hash: string; process: string; court: string; kind: string;
  available: string; text: string; link: string;
  orgao?: string; classe?: string; tipoDoc?: string; partes?: Parte[]; advogados?: string[] };

export class RateLimited extends Error {}
export class TooBroad extends Error {}

async function get(params: Record<string, string>) {
  const r = await fetch(`${BASE}?${new URLSearchParams(params).toString()}`, { headers: { Accept: 'application/json' } });
  if (r.status === 429) throw new RateLimited();
  return r;
}

const map = (x: any): Pub => ({
  hash: String(x.hash ?? x.id ?? ''),
  process: String(x.numeroprocessocommascara ?? x.numero_processo ?? ''),
  court: String(x.siglaTribunal ?? x.tribunal ?? ''),
  kind: String(x.tipoComunicacao ?? x.tipoDocumento ?? ''),
  available: String(x.data_disponibilizacao ?? x.dataDisponibilizacao ?? ''),
  text: String(x.texto ?? '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 4000),
  link: String(x.link ?? ''),
  orgao: String(x.nomeOrgao ?? x.orgao ?? '').trim(),
  classe: String(x.nomeClasse ?? x.classe ?? '').trim(),
  tipoDoc: String(x.tipoDocumento ?? '').trim(),
  partes: (Array.isArray(x.destinatarios) ? x.destinatarios : [])
    .map((d: any) => ({ nome: String(d?.nome ?? '').trim(), polo: String(d?.polo ?? '').trim() }))
    .filter((d: Parte) => d.nome).slice(0, 20),
  advogados: (Array.isArray(x.destinatarioadvogados) ? x.destinatarioadvogados : [])
    .map((d: any) => { const a = d?.advogado ?? d; const oab = a?.numero_oab ? ` (OAB ${a.numero_oab}/${a.uf_oab ?? ''})` : ''; return a?.nome ? `${String(a.nome).trim()}${oab}` : ''; })
    .filter(Boolean).slice(0, 20),
});

/** amplo: aceita buscas grandes (usado dia a dia, 0.55); traz até 1.000 publicações. */
export async function consultar(q: { name?: string; oab?: string; uf?: string; tribunal?: string }, from: Date, to: Date, amplo = false) {
  const out: Pub[] = [];
  for (let pagina = 1; pagina <= (amplo ? 10 : 5); pagina++) {
    const filtro: Record<string, string> = { itensPorPagina: '100', pagina: String(pagina) };
    if (q.name) filtro.nomeParte = q.name;
    if (q.oab && q.uf) { filtro.numeroOab = q.oab; filtro.ufOab = q.uf; }
    if (q.tribunal) filtro.siglaTribunal = q.tribunal;

    let r = await get({ ...filtro, dataDisponibilizacaoInicio: ymd(from), dataDisponibilizacaoFim: ymd(to) });
    if (r.status === 400 || r.status === 422)       // tenta o outro formato de data
      r = await get({ ...filtro, dataDisponibilizacaoInicio: br(from), dataDisponibilizacaoFim: br(to) });
    if (!r.ok) throw new Error(`DJEN ${r.status}`);

    const j: any = await r.json();
    const items: any[] = j.items ?? j.content ?? [];
    if (!amplo && pagina === 1 && Number(j.count ?? items.length) > 300) throw new TooBroad();
    out.push(...items.map(map).filter(p => p.hash));
    if (items.length < 100) break;
    await new Promise(res => setTimeout(res, 1200));
  }
  return out;
}

// ─── Querido Diário (Open Knowledge Brasil): diários oficiais dos municípios ─────────────────────
// Documentação: https://queridodiario.ok.org.br/api/docs  (GET /gazettes → { total_gazettes, gazettes: [
//   { territory_id, territory_name, state_code, date, url, txt_url?, excerpts: string[], edition?, is_extra_edition? }] })
const QD = ['https://api.queridodiario.ok.org.br/gazettes', 'https://queridodiario.ok.org.br/api/gazettes'];

/** Resumo curto e estável de um texto (para identificar o diário pelo endereço). */
export const resumo = (s: string) => { let h = 5381; for (let i = 0; i < s.length; i++) h = ((h * 33) ^ s.charCodeAt(i)) >>> 0; return h.toString(36); };

const mapDiario = (g: any): Pub | null => {
  const data = String(g?.date ?? '').slice(0, 10);
  const cidade = String(g?.territory_name ?? '').trim();
  const uf = String(g?.state_code ?? '').trim();
  const url = String(g?.url ?? g?.txt_url ?? '');
  if (!data || !url) return null;
  const trechos = (Array.isArray(g?.excerpts) ? g.excerpts : []).map((x: any) => String(x ?? '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()).filter(Boolean);
  return {
    hash: `qd:${g?.territory_id ?? cidade}:${data}:${resumo(url)}`,
    process: '', court: `DOM ${cidade}${uf ? `/${uf}` : ''}`.trim(), kind: 'Diário Oficial Municipal',
    available: data, text: trechos.join(' … ').slice(0, 4000), link: url,
  };
};

/** Procura o nome (entre aspas) nos diários oficiais municipais. Se o serviço não responder, devolve []. */
export async function consultarDiarios(nome: string, from: Date, to: Date): Promise<Pub[]> {
  const params = new URLSearchParams({
    querystring: `"${nome.replace(/"/g, '').trim()}"`, published_since: ymd(from), published_until: ymd(to),
    size: '50', excerpt_size: '600', number_of_excerpts: '3', sort_by: 'descending_date',
  }).toString();
  for (const base of QD) {
    try {
      const ctl = typeof AbortController !== 'undefined' ? new AbortController() : null;
      const tm = ctl ? setTimeout(() => ctl.abort(), 20000) : null;
      const r = await fetch(`${base}?${params}`, { headers: { Accept: 'application/json' }, signal: ctl?.signal }).finally(() => tm && clearTimeout(tm));
      if (!r.ok) continue;
      const j: any = await r.json();
      const lista: any[] = Array.isArray(j?.gazettes) ? j.gazettes : [];
      return lista.map(mapDiario).filter((p): p is Pub => !!p);
    } catch { /* tenta o outro endereço; se nenhum responder, ignora */ }
  }
  return [];
}
